import { javaFixed, javaIsBlank, javaRound, javaStrip } from '../javaText.ts';
import { JsonDouble, type JsonValue as Json } from '../jsonWriter.ts';
import { javaCompare, slugify } from '../exportData.ts';
import type { SqlConnection } from '../sql.ts';
import { NEUTRAL_COLOR, REGION_MIN_POINTS, getMap, listCategories, listIcons, listMapMarkers, listMapRegions, listMaps, regionColor, type AtlasMap, type MapMarker, type MapRegion, type MarkerCategory } from './atlas.ts';
import { listDrawings, listLayers, type MapDrawing, type MapLayer } from './drawings.ts';
import { isRevealed, parseFog, revealsAny, type Rings } from './fog.ts';
import { centroid, type MapPoint } from './geometry.ts';
import { effectiveIcon, parseIconRef, resolveIcon, type MapIcon, type ResolvedIcon } from './icons.ts';
import { formatScaled, gridSegments, type Segment } from './measure.ts';

/**
 * Atlas maps in the exports (docs/PARITY-STEP-4-PLAN.md section 11), as the desktop's
 * `RealmExportData.ExportedMap` and `export.MapExport` gather and lay them out: what's on
 * each map, which of it a rendering shows, where region names go, the caption, and the
 * maps section of the JSON export. The app draws the SVG overlay and the flattened JPEG
 * from these.
 *
 * Every export the web makes is the owner's (as the desktop's JSON, Markdown, HTML and PDF
 * are), so `includeHidden` is true for all of them; the player-facing rules (fog, GM-only
 * things left out) are kept, as the desktop has them, for an export that faces players.
 */

export interface ExportMap {
  readonly map: AtlasMap;
  /** Oldest first, as the map keeps them. */
  readonly markers: readonly MapMarker[];
  readonly regions: readonly MapRegion[];
  readonly layers: readonly MapLayer[];
  readonly drawings: readonly MapDrawing[];
  /** The image's size in pixels: the space pins and regions are placed in. */
  readonly width: number;
  readonly height: number;
  /** A file-name-safe name, unique among the exported maps. */
  readonly slug: string;
  /** The revealed part, parsed once. */
  readonly revealed: Rings;
}

/** What the map renderings need beyond the maps: categories and imported icons, by id. */
export interface ExportAtlas {
  readonly maps: readonly ExportMap[];
  readonly categories: ReadonlyMap<string, MarkerCategory>;
  readonly icons: ReadonlyMap<string, MapIcon>;
}

/** Every map, in gallery (name) order, as `RealmExportData.gather` collects them. */
export function gatherAtlas(conn: SqlConnection): ExportAtlas {
  const slugs = new Set<string>();
  const maps: ExportMap[] = [];
  for (const summary of listMaps(conn)) {
    const map = getMap(conn, summary.id);
    if (!map) continue;
    const [width, height] = imageSize(map.imageData);
    // `uniqueSlug(name, slugs, "map")`.
    const base = javaIsBlank(slugify(map.name)) ? 'map' : slugify(map.name);
    let slug = base;
    for (let n = 2; slugs.has(slug); n++) slug = `${base}-${n}`;
    slugs.add(slug);
    maps.push({
      map,
      markers: listMapMarkers(conn, map.id),
      regions: listMapRegions(conn, map.id),
      layers: listLayers(conn, map.id),
      drawings: listDrawings(conn, map.id),
      width,
      height,
      slug,
      revealed: parseFog(map.fog.revealed),
    });
  }
  return { maps, categories: new Map(listCategories(conn).map((c) => [c.id, c])), icons: new Map(listIcons(conn).map((i) => [i.id, i])) };
}

/**
 * An image's size from its header (PNG, JPEG, WebP, GIF), without decoding it; 1 by 1 when
 * it can't be read, as `RealmExportData.imageSize` falls back.
 */
export function imageSize(data: Uint8Array): [number, number] {
  const v = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const fits = (at: number, n: number) => at >= 0 && at + n <= data.byteLength;
  const ascii = (at: number, s: string) => fits(at, s.length) && [...s].every((c, i) => data[at + i] === c.charCodeAt(0));
  const sized = (w: number, h: number): [number, number] => (w > 0 && h > 0 ? [w, h] : [1, 1]);
  if (fits(0, 24) && data[0] === 0x89 && ascii(1, 'PNG') && ascii(12, 'IHDR')) return sized(v.getUint32(16), v.getUint32(20));
  if (ascii(0, 'GIF8') && fits(6, 4)) return sized(v.getUint16(6, true), v.getUint16(8, true));
  if (ascii(0, 'RIFF') && ascii(8, 'WEBP') && fits(12, 18)) {
    if (ascii(12, 'VP8 ') && fits(26, 4)) return sized(v.getUint16(26, true) & 0x3fff, v.getUint16(28, true) & 0x3fff);
    if (ascii(12, 'VP8L') && fits(21, 4)) {
      const bits = v.getUint32(21, true);
      return sized((bits & 0x3fff) + 1, ((bits >>> 14) & 0x3fff) + 1);
    }
    if (ascii(12, 'VP8X') && fits(24, 6)) {
      const u24 = (at: number) => data[at]! | (data[at + 1]! << 8) | (data[at + 2]! << 16);
      return sized(u24(24) + 1, u24(27) + 1);
    }
    return [1, 1];
  }
  if (fits(0, 4) && data[0] === 0xff && data[1] === 0xd8) {
    let at = 2;
    while (fits(at, 4)) {
      if (data[at] !== 0xff) {
        at++;
        continue;
      }
      const marker = data[at + 1]!;
      if (marker === 0xff) {
        at++;
        continue;
      }
      if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
        at += 2;
        continue;
      }
      const length = v.getUint16(at + 2);
      // Start of frame: every SOFn but DHT (C4), JPG (C8) and DAC (CC).
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
        return fits(at + 5, 4) ? sized(v.getUint16(at + 7), v.getUint16(at + 5)) : [1, 1];
      }
      if (marker === 0xd9 || marker === 0xda) break;
      at += 2 + length;
    }
  }
  return [1, 1];
}

// ---- what a rendering shows (`MapExport.shown`) ----

/** Whether this rendering faces players and the map has fog of war on. */
export const fogged = (m: ExportMap, includeHidden: boolean) => !includeHidden && m.map.fog.enabled;

export const pinShown = (m: ExportMap, pin: MapMarker, includeHidden: boolean) =>
  (pin.visible || includeHidden) && (includeHidden || isRevealed(m.map.fog.enabled, m.revealed, pin));

export const regionShown = (m: ExportMap, region: MapRegion, includeHidden: boolean) =>
  (region.visible || includeHidden) && region.points.length >= REGION_MIN_POINTS && (includeHidden || revealsAny(m.map.fog.enabled, m.revealed, region.points));

export const drawingShown = (m: ExportMap, drawing: MapDrawing, includeHidden: boolean) => includeHidden || revealsAny(m.map.fog.enabled, m.revealed, drawing.points);

/** The drawings on layers players may see, or every drawing with `includeHidden` (`ExportedMap.drawings`). */
export function exportDrawings(m: ExportMap, includeHidden: boolean): MapDrawing[] {
  if (includeHidden) return [...m.drawings];
  const shown = new Set(m.layers.filter((l) => l.playersCanSee).map((l) => l.id));
  return m.drawings.filter((d) => shown.has(d.layerId));
}

/** Pins by label, without regard to case (`sortedPins`, a stable sort). */
export const sortedPins = (m: ExportMap) => [...m.markers].sort((a, b) => javaCompare(a.label.toLowerCase(), b.label.toLowerCase()));

export const pinColor = (atlas: ExportAtlas, pin: MapMarker) => (pin.categoryId ? atlas.categories.get(pin.categoryId)?.colorHex : undefined) ?? NEUTRAL_COLOR;

export const exportRegionColor = (atlas: ExportAtlas, region: MapRegion) => regionColor(region, region.categoryId ? atlas.categories.get(region.categoryId) : undefined);

export const categoryOf = (atlas: ExportAtlas, id: string | null) => (id ? atlas.categories.get(id) : undefined);

/** A pin's own icon, else its category's (`RealmExportData.pinIcon`). */
export function pinIcon(atlas: ExportAtlas, pin: MapMarker): ResolvedIcon | null {
  return resolveIcon(effectiveIcon(pin.icon, categoryOf(atlas, pin.categoryId)?.icon ?? ''), (id) => atlas.icons.get(id));
}

/** A category's icon when it's an emoji, which reads as text in a list of places. */
export function categoryEmoji(atlas: ExportAtlas, id: string | null): string | null {
  const ref = parseIconRef(categoryOf(atlas, id)?.icon);
  return ref.kind === 'EMOJI' ? ref.text : null;
}

const GRID_NAMES: Readonly<Record<string, string>> = { NONE: 'No grid', SQUARE: 'Square', HEX: 'Hex' };

/** "The map is 1,200 miles across · Hex grid, 64 px cells (48 miles)", or "" if neither is set (`caption`). */
export function mapCaption(m: ExportMap): string {
  const parts: string[] = [];
  const { scale, grid } = m.map;
  const set = scale.unitsPerPixel > 0;
  if (set) parts.push(`The map is ${formatScaled(scale, m.width)} across`);
  if (grid.type !== 'NONE') parts.push(`${GRID_NAMES[grid.type]} grid, ${javaRound(grid.cellSize)} px cells${set ? ` (${formatScaled(scale, grid.cellSize)})` : ''}`);
  return parts.join(' · ');
}

export const mapGrid = (m: ExportMap): Segment[] => gridSegments(m.map.grid, m.width, m.height);

/** `MapExport.num`: whole numbers whole, others to two places. */
export const svgNumber = (value: number) => (value === Math.round(value) && Number.isFinite(value) ? String(Math.trunc(value)) : javaFixed(value, 2));

/** "Label (GM only)" and the notes on the next line: a pin's or region's hover text (`tooltip`). */
export function placeTooltip(label: string, notes: string, visible: boolean): string {
  let text = javaIsBlank(label) ? '(unlabeled)' : label;
  if (!visible) text += ' (GM only)';
  return javaIsBlank(notes) ? text : `${text}\n${notes}`;
}

/** The maps an entry is pinned or outlined on, for "Appears on" (`mapsShowing`). */
export function mapsShowing(atlas: ExportAtlas, entryId: string, includeHidden: boolean): ExportMap[] {
  return atlas.maps.filter(
    (m) => m.markers.some((p) => p.linkedEntryId === entryId && pinShown(m, p, includeHidden)) || m.regions.some((r) => r.linkedEntryId === entryId && regionShown(m, r, includeHidden)),
  );
}

/** A file extension for a map's image, from its type (`extension`). */
export const mapImageExtension = (m: ExportMap) => (m.map.mimeType === 'image/jpeg' ? 'jpg' : m.map.mimeType === 'image/webp' ? 'webp' : 'png');

const NUDGES = [0, -34, 34, -60, 60, -86, 86];
type Box = readonly [number, number, number, number];

const textBox = (x: number, y: number, text: string, fontSize: number): Box => {
  const half = text.length * fontSize * 0.29;
  return [x - half, y - fontSize * 0.75, x + half, y + fontSize * 0.35];
};
const intersects = (a: Box, b: Box) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];

/**
 * Where each region's name goes, in map pixels (`regionLabelPositions`): at its centroid,
 * dropped where a pin right there already carries it, else nudged up or down until it
 * clears every shown pin, pin label and placed name, and left out if nothing fits.
 * `pinScale` is how large pins are drawn against their 26 by 35 design, in map pixels.
 */
export function regionLabelPositions(m: ExportMap, includeHidden: boolean, pinScale: number): Map<MapRegion, MapPoint> {
  const occupied: Box[] = [];
  for (const pin of m.markers) {
    if (!pinShown(m, pin, includeHidden)) continue;
    occupied.push([pin.x - 13 * pinScale, pin.y - 35 * pinScale, pin.x + 13 * pinScale, pin.y]);
    if (!javaIsBlank(pin.label)) occupied.push(textBox(pin.x, pin.y + 16 * pinScale, pin.label, 12 * pinScale));
  }
  const positions = new Map<MapRegion, MapPoint>();
  for (const region of m.regions) {
    if (!regionShown(m, region, includeHidden) || javaIsBlank(region.label)) continue;
    const c = centroid(region.points);
    if (fogged(m, includeHidden) && !isRevealed(m.map.fog.enabled, m.revealed, c)) continue;
    const duplicate = m.markers.some(
      (p) => pinShown(m, p, includeHidden) && p.label.toLowerCase() === region.label.toLowerCase() && Math.hypot(p.x - c.x, p.y - c.y) < 70 * pinScale,
    );
    if (duplicate) continue;
    for (const nudge of NUDGES) {
      const y = c.y + nudge * pinScale;
      const box = textBox(c.x, y, region.label, 18 * pinScale);
      if (occupied.every((o) => !intersects(o, box))) {
        occupied.push(box);
        positions.set(region, { x: c.x, y });
        break;
      }
    }
  }
  return positions;
}

// ---- the JSON export's maps (`JsonExporter.mapsJson`) ----

const d = (value: number) => new JsonDouble(value);
const pointsJson = (points: readonly MapPoint[]): Json => points.map((p) => [d(p.x), d(p.y)]);

export function mapsJson(atlas: ExportAtlas, entryTitles: ReadonlyMap<string, string>, base64: (bytes: Uint8Array) => string): Json[] {
  const place = (label: string, categoryId: string | null, notes: string, visible: boolean, entryId: string | null, mapId: string | null) => {
    const category = categoryOf(atlas, categoryId);
    return new Map<string, Json>([
      ['label', label],
      ['category', category?.name ?? null],
      ['categoryIcon', category?.icon ?? null],
      ['notes', notes],
      ['gmOnly', !visible],
      ['linkedEntryId', entryId],
      ['linkedEntryTitle', entryId !== null ? (entryTitles.get(entryId) ?? null) : null],
      ['linkedMap', (mapId !== null ? atlas.maps.find((m) => m.map.id === mapId)?.map.name : undefined) ?? null],
    ]);
  };
  return atlas.maps.map((m) => {
    const { map } = m;
    const json = new Map<string, Json>([
      ['id', map.id],
      ['name', map.name],
      ['width', m.width],
      ['height', m.height],
      ['scale', new Map<string, Json>([['unitsPerPixel', d(map.scale.unitsPerPixel)], ['unit', map.scale.unit]])],
      ['grid', new Map<string, Json>([['type', map.grid.type], ['cellSize', d(map.grid.cellSize)], ['color', map.grid.colorHex], ['opacity', d(map.grid.opacity)]])],
      ['fog', new Map<string, Json>([['enabled', map.fog.enabled], ['revealed', m.revealed.map(pointsJson)]])],
      [
        'pins',
        m.markers.map((pin) => {
          const p = place(pin.label, pin.categoryId, pin.notes, pin.visible, pin.linkedEntryId, pin.linkedMapId);
          p.set('x', d(pin.x));
          p.set('y', d(pin.y));
          p.set('icon', pin.icon === '' ? null : pin.icon);
          p.set('style', pin.style.toLowerCase());
          return p;
        }),
      ],
      [
        'drawingLayers',
        m.layers.map(
          (layer) =>
            new Map<string, Json>([
              ['name', layer.name],
              ['playersCanSee', layer.playersCanSee],
              [
                'drawings',
                m.drawings
                  .filter((dr) => dr.layerId === layer.id)
                  .map(
                    (dr) =>
                      new Map<string, Json>([
                        ['kind', dr.kind.toLowerCase()],
                        ['points', pointsJson(dr.points)],
                        ['color', dr.strokeColor],
                        ['width', d(dr.strokeWidth)],
                        ['dashed', dr.dashed],
                        ['fill', dr.fillColor],
                        ['fillOpacity', dr.fillColor === null ? null : d(dr.fillOpacity)],
                        ['text', dr.text === '' ? null : dr.text],
                        ['textSize', dr.kind === 'TEXT' ? d(dr.textSize) : null],
                      ]),
                  ),
              ],
            ]),
        ),
      ],
      [
        'regions',
        m.regions.map((region) => {
          const r = place(region.label, region.categoryId, region.notes, region.visible, region.linkedEntryId, region.linkedMapId);
          r.set('color', exportRegionColor(atlas, region));
          r.set('points', pointsJson(region.points));
          return r;
        }),
      ],
      ['imageMimeType', map.mimeType],
      ['imageBase64', base64(map.imageData)],
    ]);
    return json;
  });
}

/** A note's text on one line, as a list of places shows it (`notes.strip().replace("\n", " ")`). */
export const oneLine = (notes: string) => javaStrip(notes).replaceAll('\n', ' ');
